import React from "react";
import { Link } from "react-router-dom";
import "../../assets/css/Cartattva.css";

import processControlLogo from "../../assets/img/clients/skill logo.jpg";

/* =========================================================
   PROCESS CONTROL — SKILL GAUGES
========================================================= */

const workflow = [
  {
    number: "01",
    title: "Quotation Management",
    text: "Create and manage customer quotations with structured commercial and product information.",
  },
  {
    number: "02",
    title: "Revised Quotation",
    text: "Manage quotation revisions while maintaining visibility into updated commercial requirements.",
  },
  {
    number: "03",
    title: "Purchase Order",
    text: "Convert approved quotations into purchase orders and maintain a connected order workflow.",
  },
  {
    number: "04",
    title: "Bill of Materials",
    text: "Manage BOM information required for manufacturing and production planning.",
  },
  {
    number: "05",
    title: "Process Vouchers",
    text: "Track manufacturing processes and production activities through structured process vouchers.",
  },
  {
    number: "06",
    title: "Finished Goods",
    text: "Manage finished goods generated from completed manufacturing and process activities.",
  },
  {
    number: "07",
    title: "Billing",
    text: "Connect completed orders and finished goods with the billing workflow for streamlined invoicing.",
  },
];

const technologies = [
  "React.js",
  "Node.js",
  "SQL Server Management Studio (SSMS)",
];

const businessImpact = [
  "Connected quotation-to-billing workflow",
  "Reduced dependency on disconnected manual processes",
  "Centralized manufacturing and order information",
  "Improved visibility across production stages",
  "Structured BOM and process management",
  "Better tracking of finished goods",
  "Improved operational reporting",
  "Streamlined billing workflow",
];

const modules = [
  "Quotation Management",
  "Revised Quotation Management",
  "Purchase Order Management",
  "Bill of Materials (BOM)",
  "Process Voucher Management",
  "Finished Goods Inventory",
  "Billing & Invoicing",
];

/* =========================================================
   COMPONENT
========================================================= */

export default function ProcessControl() {
  const siteUrl = "https://hexxatech.co.in";
  const pageUrl = `${siteUrl}/case-studies/process-control`;

  const title =
    "Process Control & Manufacturing Management Software for Skill Gauges | HexxaTech";

  const description =
    "Explore how HexxaTech Softwares developed a custom process control and manufacturing management system for Skill Gauges, connecting quotations, revised quotations, purchase orders, BOM, process vouchers, finished goods and billing.";

  const organizationId = `${siteUrl}#organization`;
  const websiteId = `${siteUrl}#website`;

  /* =======================================================
     STRUCTURED DATA
  ======================================================= */

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: title,
        description,
        isPartOf: {
          "@id": websiteId,
        },
        about: {
          "@id": `${pageUrl}#software`,
        },
        breadcrumb: {
          "@id": `${pageUrl}#breadcrumb`,
        },
        inLanguage: "en-IN",
      },

      {
        "@type": "SoftwareApplication",
        "@id": `${pageUrl}#software`,
        name: "Process Control & Manufacturing Management System",
        alternateName: "Skill Gauges Process Control System",
        description:
          "A custom manufacturing and process management software system developed for Skill Gauges to connect quotations, revised quotations, purchase orders, BOM, process vouchers, finished goods and billing.",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        creator: {
          "@id": organizationId,
        },
        softwareRequirements:
          "React.js, Node.js, SQL Server",
      },

      {
        "@type": "Organization",
        "@id": organizationId,
        name: "HexxaTech Softwares",
        url: siteUrl,
      },

      {
        "@type": "WebSite",
        "@id": websiteId,
        url: siteUrl,
        name: "HexxaTech Softwares",
        publisher: {
          "@id": organizationId,
        },
      },

      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumb`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: siteUrl,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Case Studies",
            item: `${siteUrl}/case-studies`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Process Control System",
            item: pageUrl,
          },
        ],
      },

      {
        "@type": "Article",
        "@id": `${pageUrl}#article`,
        headline:
          "Process Control & Manufacturing Management Software for Skill Gauges",
        description,
        author: {
          "@id": organizationId,
        },
        publisher: {
          "@id": organizationId,
        },
        mainEntityOfPage: {
          "@id": `${pageUrl}#webpage`,
        },
      },
    ],
  };

  return (
    <>
      {/* =====================================================
          SEO
      ====================================================== */}

      <title>{title}</title>

      <meta
        name="description"
        content={description}
      />

      <meta
        name="robots"
        content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      />

      <link
        rel="canonical"
        href={pageUrl}
      />

      {/* =====================================================
          OPEN GRAPH
      ====================================================== */}

      <meta
        property="og:title"
        content="Process Control & Manufacturing Management Software | Skill Gauges"
      />

      <meta
        property="og:description"
        content={description}
      />

      <meta
        property="og:url"
        content={pageUrl}
      />

      <meta
        property="og:type"
        content="article"
      />

      <meta
        property="og:site_name"
        content="HexxaTech Softwares"
      />

      <meta
        property="og:locale"
        content="en_IN"
      />

      {/* =====================================================
          TWITTER
      ====================================================== */}

      <meta
        name="twitter:card"
        content="summary_large_image"
      />

      <meta
        name="twitter:title"
        content="Process Control & Manufacturing Software | HexxaTech"
      />

      <meta
        name="twitter:description"
        content={description}
      />

      {/* =====================================================
          STRUCTURED DATA
      ====================================================== */}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(structuredData),
        }}
      />

      {/* =====================================================
          PAGE
      ====================================================== */}

      <main className="cartattva-page process-control-page">

        {/* =================================================
            HERO
        ================================================== */}

        <section className="cartattva-hero">

          <div className="cartattva-hero-glow cartattva-glow-one" />
          <div className="cartattva-hero-glow cartattva-glow-two" />

          <div className="cartattva-container">

            {/* Breadcrumb */}

            <nav
              className="cartattva-back"
              aria-label="Breadcrumb"
            >
              <Link to="/case-studies">
                ← Back to Case Studies
              </Link>
            </nav>

            <div className="cartattva-hero-content">

              {/* Client Logo */}

              <div className="cartattva-logo-wrapper">
                <img
                  src={processControlLogo}
                  alt="Skill Gauges logo"
                  className="cartattva-logo"
                  width="180"
                  height="100"
                  loading="eager"
                />
              </div>

              <span className="cartattva-eyebrow">
                <span className="cartattva-eyebrow-dot" />
                Manufacturing Software Case Study
              </span>

              <h1>
                Process Control &
                <br />
                Manufacturing
                <br />
                Management System
              </h1>

              <div className="cartattva-title-line" />

              <h2>
                A custom manufacturing workflow
                platform developed for Skill Gauges
              </h2>

              <p>
                HexxaTech Softwares developed a centralized
                process control and manufacturing management
                system for Skill Gauges, connecting the
                complete business workflow from quotation
                and revised quotation through purchase orders,
                BOM, process vouchers, finished goods and
                billing.
              </p>

              <div className="cartattva-hero-tags">
                <span>Manufacturing Software</span>
                <span>Process Management</span>
                <span>ERP Workflow</span>
                <span>React + Node.js</span>
              </div>

            </div>
          </div>
        </section>

        {/* =================================================
            PROJECT OVERVIEW
        ================================================== */}

        <section className="cartattva-overview">

          <div className="cartattva-container">

            <div className="cartattva-section-label">
              Project Overview
            </div>

            <h2 className="cartattva-section-title">
              Connecting the complete manufacturing
              workflow in one system.
            </h2>

            <div className="cartattva-overview-grid">

              <article className="cartattva-info-card">

                <span>THE BUSINESS NEED</span>

                <h3>
                  Managing multiple stages of the
                  manufacturing order lifecycle
                </h3>

                <p>
                  Manufacturing operations involve multiple
                  connected stages, from preparing quotations
                  and handling revisions to purchase orders,
                  material requirements, production processes,
                  finished goods and billing.
                </p>

                <p>
                  Skill Gauges required a centralized software
                  solution that could connect these workflows
                  and provide better visibility across the
                  business process.
                </p>

              </article>

              <article className="cartattva-info-card solution">

                <span>OUR SOLUTION</span>

                <h3>
                  A connected quotation-to-billing
                  workflow
                </h3>

                <p>
                  HexxaTech developed a custom web-based
                  process control system that connects the
                  major stages of the manufacturing workflow.
                </p>

                <p>
                  The system links quotations, revised
                  quotations, purchase orders, bills of
                  materials, process vouchers, finished goods
                  and billing into a structured operational
                  workflow.
                </p>

              </article>

            </div>
          </div>
        </section>

        {/* =================================================
            WORKFLOW
        ================================================== */}

        <section className="cartattva-features">

          <div className="cartattva-container">

            <div className="cartattva-section-label">
              Manufacturing Workflow
            </div>

            <h2 className="cartattva-section-title">
              From quotation to finished goods and billing.
            </h2>

            <div className="cartattva-feature-grid">

              {workflow.map((item) => (
                <article
                  className="cartattva-feature-card"
                  key={item.number}
                >

                  <span className="feature-number">
                    {item.number}
                  </span>

                  <h3>
                    {item.title}
                  </h3>

                  <p>
                    {item.text}
                  </p>

                </article>
              ))}

            </div>

          </div>
        </section>

        {/* =================================================
            END-TO-END WORKFLOW
        ================================================== */}

        <section className="cartattva-technology">

          <div className="cartattva-container">

            <div className="cartattva-section-label">
              End-to-End Workflow
            </div>

            <h2 className="cartattva-section-title">
              A connected business process.
            </h2>

            <p className="technology-description">
              The platform creates a structured workflow
              connecting commercial, purchasing, production,
              finished goods and billing activities.
            </p>

            <div className="technology-list">

              {[
                "Quotation",
                "Revised Quotation",
                "Purchase Order",
                "BOM",
                "Process Vouchers",
                "Finished Goods",
                "Billing",
              ].map((item, index) => (
                <React.Fragment key={item}>

                  <span>
                    {item}
                  </span>

                  {index < 6 && (
                    <strong
                      aria-hidden="true"
                      style={{
                        fontSize: "20px",
                        opacity: 0.5,
                      }}
                    >
                      →
                    </strong>
                  )}

                </React.Fragment>
              ))}

            </div>

          </div>
        </section>

        {/* =================================================
            MODULES
        ================================================== */}

        <section className="cartattva-impact">

          <div className="cartattva-container">

            <div className="cartattva-section-label">
              Core Modules
            </div>

            <h2 className="cartattva-section-title">
              Software designed around the
              manufacturing operation.
            </h2>

            <div className="impact-grid">

              {modules.map((module) => (
                <div
                  className="impact-item"
                  key={module}
                >

                  <span className="impact-check">
                    ✓
                  </span>

                  <span>
                    {module}
                  </span>

                </div>
              ))}

            </div>

          </div>
        </section>

        {/* =================================================
            TECHNOLOGY STACK
        ================================================== */}

        <section className="cartattva-technology">

          <div className="cartattva-container">

            <div className="cartattva-section-label">
              Technology
            </div>

            <h2 className="cartattva-section-title">
              Built with modern web technologies.
            </h2>

            <p className="technology-description">
              The Skill Gauges process control platform was
              developed using a modern web application
              architecture designed to support business
              workflows, data management and scalable
              application development.
            </p>

            <div className="technology-list">

              {technologies.map((technology) => (
                <span key={technology}>
                  {technology}
                </span>
              ))}

            </div>

          </div>
        </section>

        {/* =================================================
            BUSINESS IMPACT
        ================================================== */}

        <section className="cartattva-impact">

          <div className="cartattva-container">

            <div className="cartattva-section-label">
              Business Impact
            </div>

            <h2 className="cartattva-section-title">
              Improving visibility across the
              manufacturing lifecycle.
            </h2>

            <div className="impact-grid">

              {businessImpact.map((impact) => (
                <div
                  className="impact-item"
                  key={impact}
                >

                  <span className="impact-check">
                    ✓
                  </span>

                  <span>
                    {impact}
                  </span>

                </div>
              ))}

            </div>

          </div>
        </section>

        {/* =================================================
            SEO CONTENT
        ================================================== */}

        <section className="cartattva-overview">

          <div className="cartattva-container">

            <div className="cartattva-section-label">
              Custom Manufacturing Software
            </div>

            <h2 className="cartattva-section-title">
              A manufacturing management platform
              built around the client's workflow.
            </h2>

            <div className="cartattva-overview-grid">

              <article className="cartattva-info-card">

                <h3>
                  Custom Process Control Software
                </h3>

                <p>
                  Generic business software does not always
                  match the operational requirements of
                  manufacturing companies. The Skill Gauges
                  system was developed around its specific
                  quotation, purchasing, production and billing
                  workflow.
                </p>

                <p>
                  This approach allows business processes to
                  remain connected while providing a single
                  platform for managing operational information.
                </p>

              </article>

              <article className="cartattva-info-card solution">

                <h3>
                  Manufacturing Software Development
                  by HexxaTech
                </h3>

                <p>
                  HexxaTech Softwares develops custom ERP,
                  manufacturing management, process control,
                  inventory and business automation software
                  for organizations with specialized workflows.
                </p>

                <p>
                  Our solutions can be designed around existing
                  business processes and can connect commercial,
                  operational and reporting activities within
                  one application.
                </p>

                <Link
                  to="/case-studies"
                  className="cartattva-cta-button"
                  style={{
                    display: "inline-flex",
                    marginTop: "15px",
                  }}
                >
                  Explore More Case Studies
                  <span>→</span>
                </Link>

              </article>

            </div>

          </div>
        </section>

        {/* =================================================
            RELATED INTERNAL LINKS
        ================================================== */}

        <section className="cartattva-overview">

          <div className="cartattva-container">

            <div className="cartattva-section-label">
              Explore HexxaTech
            </div>

            <h2 className="cartattva-section-title">
              More custom software solutions.
            </h2>

            <div className="cartattva-feature-grid">

              <article className="cartattva-feature-card">

                <h3>
                  Custom Software Development
                </h3>

                <p>
                  Explore custom software solutions designed
                  around specific business requirements.
                </p>

                <Link to="/#services">
                  Explore Services →
                </Link>

              </article>

              <article className="cartattva-feature-card">

                <h3>
                  Case Studies
                </h3>

                <p>
                  Explore other real-world software platforms
                  developed by HexxaTech Softwares.
                </p>

                <Link to="/case-studies">
                  View Case Studies →
                </Link>

              </article>

              <article className="cartattva-feature-card">

                <h3>
                  Discuss Your Software Project
                </h3>

                <p>
                  Have a manufacturing or business workflow
                  that needs custom software?
                </p>

                <Link to="/#contact">
                  Contact HexxaTech →
                </Link>

              </article>

            </div>

          </div>
        </section>

        {/* =================================================
            RELATED SERVICES
        ================================================== */}

        <section className="cartattva-overview">

          <div className="cartattva-container">

            <div className="cartattva-section-label">
              Related Services
            </div>

            <h2 className="cartattva-section-title">
              Services that align with this manufacturing workflow.
            </h2>

            <div className="cartattva-feature-grid">

              <article className="cartattva-feature-card">
                <h3>Industrial Process Software</h3>
                <p>Connect quotations, purchase orders, production, finished goods, invoicing and payments.</p>
                <Link to="/services/industrial-process-software">Explore Industrial Process Software →</Link>
              </article>

              <article className="cartattva-feature-card">
                <h3>ERP & Business Management</h3>
                <p>Unify operational, commercial and reporting data in a connected business system.</p>
                <Link to="/services/erp-business-management">Explore ERP & Business Management →</Link>
              </article>

              <article className="cartattva-feature-card">
                <h3>Inventory Management</h3>
                <p>Track materials, stock movement and transaction visibility across the production workflow.</p>
                <Link to="/services/inventory-management">Explore Inventory Management →</Link>
              </article>

            </div>

          </div>
        </section>

        {/* =================================================
            CTA
        ================================================== */}

        <section className="cartattva-cta">

          <div className="cartattva-cta-glow" />

          <div className="cartattva-container">

            <div className="cartattva-cta-inner">

              <span className="cta-label">
                Start Your Project
              </span>

              <h2>
                Need custom manufacturing
                <br />
                software for your business?
              </h2>

              <p>
                Tell us about your quotation, production,
                inventory or billing workflow. HexxaTech can
                help design and develop a custom software
                solution around your business processes.
              </p>

              <Link
                to="/#contact"
                className="cartattva-cta-button"
              >
                Discuss Your Project
                <span>→</span>
              </Link>

            </div>

          </div>
        </section>

        {/* =================================================
            FOOTER NOTE
        ================================================== */}

        <div className="cartattva-footer-note">
          Process Control System developed for Skill Gauges
          by HexxaTech Softwares
        </div>

      </main>
    </>
  );
}